import { Art, BeltRank, Uniform, type Technique } from '../types';
import { TIER_LABELS } from '../constants/layout';
import genericTierLabels from './tierLabels.json';

// Registry of supported grappling arts. Adding an art = adding an entry here
// (plus content tagged with its id). Everything art-specific in the UI —
// labels, tier naming, which filters make sense — routes through this file.
export interface ArtMeta {
  id: Art;
  /** Full display name, e.g. picker cards and Settings. */
  label: string;
  /** Compact form for chips and subtitles. */
  shortLabel: string;
  /** Optional one-liner. Not shown in the picker (which is name-only) — kept
   *  for the founding arts and available anywhere a description is wanted. */
  tagline?: string;
  /** Names for the five numeric tiers (Technique.tier). */
  tierLabels: Record<BeltRank, string>;
  /** Whether the Gi/No-Gi filter is meaningful for this art. */
  showUniformFilter: boolean;
  /** The only uniform this art is ever done in — Judo is a gi sport, Wrestling
   *  a no-gi one. Left undefined for arts trained both ways (BJJ), which are
   *  the only ones a submitter has to answer the question for. */
  uniform?: Uniform;
}

/** Neutral tier names for arts without a defined ranking presentation. The
 *  names live in tierLabels.json so the reel generator (plain node) reads the
 *  same file rather than keeping its own copy. See scripts/make-reels.mjs. */
export const GENERIC_TIER_LABELS: Record<BeltRank, string> =
  genericTierLabels as Record<BeltRank, string>;

// Sorted alphabetically by label at load (see below), so the picker and every
// art-chip list read in the same predictable order no matter where a new entry
// is added here. Only BJJ has its own tier naming (belts); everything else uses
// generic levels until it earns its own.
export const ARTS: ArtMeta[] = [
  {
    id: Art.Bjj,
    label: 'Jiu-Jitsu',
    shortLabel: 'Jiu-Jitsu',
    tagline: 'Guards, passes and submissions in gi and no-gi.',
    tierLabels: TIER_LABELS,
    showUniformFilter: true,
  },
  {
    id: Art.Judo,
    label: 'Judo',
    shortLabel: 'Judo',
    tagline: 'Throws, grips and pins.',
    tierLabels: GENERIC_TIER_LABELS,
    showUniformFilter: false,
    uniform: Uniform.Gi,
  },
  {
    id: Art.Wrestling,
    label: 'Wrestling',
    shortLabel: 'Wrestling',
    tagline: 'Takedowns, rides and scrambles.',
    tierLabels: GENERIC_TIER_LABELS,
    showUniformFilter: false,
    uniform: Uniform.NoGi,
  },
  // Sambo is hidden until there's content for it — an art card that opens to an
  // empty library reads as broken. Art.Sambo stays in the enum and the server's
  // VALID_ARTS, so restoring it is just uncommenting this block.
  // {
  //   id: Art.Sambo,
  //   label: 'Sambo',
  //   shortLabel: 'Sambo',
  //   tagline: 'Jacket wrestling with throws and leg locks.',
  //   tierLabels: GENERIC_TIER_LABELS,
  //   showUniformFilter: false,
  //   uniform: Uniform.Gi,
  // },
].sort((a, b) => a.label.localeCompare(b.label));

export const ART_META: Record<Art, ArtMeta> = Object.fromEntries(
  ARTS.map((a) => [a.id, a])
) as Record<Art, ArtMeta>;

/** All five tiers, in order. Kept here so the rung helper below and every
 *  caller agree on what "the scale" is. */
export const TIERS: BeltRank[] = [
  BeltRank.WhiteBelt,
  BeltRank.BlueBelt,
  BeltRank.PurpleBelt,
  BeltRank.BrownBelt,
  BeltRank.BlackBelt,
];

/**
 * The scale as a user sees it: one rung per distinct label, lowest first.
 *
 * Two tiers can share a name. The neutral scale runs Beginner, Intermediate,
 * Advanced, Expert across five stored tiers, so the bottom two are both
 * Beginner — a clip filed either side of that line means the same thing to a
 * judoka. Belts are all distinct, so for BJJ this is just the five, unchanged.
 *
 * Anything that lists levels — the filter chips, the level selects — must build
 * from this rather than from the label map, or the merged rung appears twice.
 */
export function tierRungs(labels: Record<BeltRank, string>): { tier: BeltRank; label: string; tiers: BeltRank[] }[] {
  const rungs: { tier: BeltRank; label: string; tiers: BeltRank[] }[] = [];
  for (const t of TIERS) {
    const last = rungs[rungs.length - 1];
    if (last && last.label === labels[t]) last.tiers.push(t);
    else rungs.push({ tier: t, label: labels[t], tiers: [t] });
  }
  return rungs;
}

/**
 * Tier labels for a multi-art selection. Only uses an art's own naming when
 * every selected art agrees (i.e. BJJ alone gets belts); a mixed selection
 * falls back to generic levels, since "Blue Belt" is meaningless for Wrestling.
 */
export function getTierLabelsFor(arts: Art[]): Record<BeltRank, string> {
  if (arts.length === 0) return GENERIC_TIER_LABELS;
  const first = ART_META[arts[0]].tierLabels;
  return arts.every((a) => ART_META[a].tierLabels === first) ? first : GENERIC_TIER_LABELS;
}

/** Gi/No-Gi is offered when it's meaningful for at least one selected art. */
export function showUniformFilterFor(arts: Art[]): boolean {
  return arts.some((a) => ART_META[a].showUniformFilter);
}

/**
 * The uniform a selection settles on its own — Judo is always gi, Wrestling
 * always no-gi — or null when there's a real question to answer: nothing
 * selected, an art trained both ways (BJJ), or two arts that disagree.
 *
 * Submission forms use this to fill the field in and drop it from the form
 * rather than ask a wrestler whether their clip was gi.
 */
export function impliedUniform(arts: Art[]): Uniform | null {
  if (arts.length === 0) return null;
  const first = ART_META[arts[0]].uniform;
  if (!first) return null;
  return arts.every((a) => ART_META[a].uniform === first) ? first : null;
}


/** Prose selection label for sentences: "Judo and Wrestling", "A, B and C". */
export function artsLabel(arts: Art[]): string {
  const names = arts.map((a) => ART_META[a].label);
  if (names.length === 0) return 'no arts';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Arts a technique belongs to, defaulting legacy (untagged) data to BJJ. */
export function techArts(t: Technique): Art[] {
  return t.arts && t.arts.length > 0 ? t.arts : [Art.Bjj];
}

/** Does a technique belong to any of the selected arts? */
export function techInArts(t: Technique, arts: Art[]): boolean {
  return techArts(t).some((a) => arts.includes(a));
}

export function isValidArt(v: unknown): v is Art {
  return typeof v === 'string' && ARTS.some((a) => a.id === v);
}

/** Keeps a selection in registry order so labels/chips read consistently. */
export function sortArts(arts: Art[]): Art[] {
  return ARTS.map((a) => a.id).filter((id) => arts.includes(id));
}
